<script>
import { GlLoadingIcon, GlLink, GlIcon } from '@gitlab/ui';
import * as Sentry from '@sentry/browser';
import getBillingAccountQuery from '~/javascripts/billing_account/graphql/queries/get_billing_account.query.graphql';
import ErrorAlert from '~/javascripts/components/error_alert.vue';
import { BILLING_ACCOUNT_PATH } from '~/javascripts/constants';
import { BILLING_ACCOUNT_ERROR_DICTIONARY } from '~/javascripts/billing_account/error_dictionary';
import { extractActiveModelError } from '~/javascripts/lib/error_utils';
import ContactBillingAddress from './contact_billing_address.vue';
import CompanyInformation from './company_information.vue';

export default {
  name: 'BillingAccountDetails',
  components: {
    GlLoadingIcon,
    ErrorAlert,
    GlLink,
    GlIcon,
    CompanyInformation,
    ContactBillingAddress,
  },
  inject: ['zuoraAccountNumber'],
  props: {
    showHeader: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      billingAccount: {
        zuoraAccountName: '',
        zuoraAccountVatId: '',
        vatFieldVisible: false,
        soldToContact: {},
        billToContact: {},
      },
      error: null,
    };
  },
  apollo: {
    billingAccount: {
      query: getBillingAccountQuery,
      variables() {
        return {
          zuoraAccountNumber: this.zuoraAccountNumber,
        };
      },
      skip() {
        return !this.zuoraAccountNumber;
      },
      update(data) {
        return data.billingAccount || {};
      },
      error(error) {
        const activeModelError = extractActiveModelError(error);
        this.handleError(activeModelError);
      },
    },
  },
  computed: {
    isLoading() {
      return this.$apollo.queries.billingAccount?.loading;
    },
    showBillingContact() {
      return this.billingAccount.soldToContact?.id !== this.billingAccount.billToContact?.id;
    },
    subscriptionContactTitle() {
      return this.showBillingContact
        ? this.$options.i18n.subscriptionContactTitle
        : this.$options.i18n.subscriptionAndBillingContactTitle;
    },
    hiddenJH() {
      return !gon.global?.jh;
    },
  },
  methods: {
    clearError() {
      this.error = null;
    },
    handleError(error) {
      this.error = error;

      Sentry.captureException(error);
    },
  },
  i18n: {
    subscriptionContactTitle: 'Subscription contact',
    billingContactTitle: 'Billing contact',
    subscriptionAndBillingContactTitle: 'Subscription and billing contact',
  },
  BILLING_ACCOUNT_PATH,
  BILLING_ACCOUNT_ERROR_DICTIONARY,
};
</script>
<template>
  <div v-if="hiddenJH" data-testid="billing-account-details">
    <error-alert
      v-if="error"
      :dismissible="true"
      :error-dictionary="$options.BILLING_ACCOUNT_ERROR_DICTIONARY"
      :error="error"
      @dismiss="clearError"
    />

    <gl-loading-icon v-if="isLoading" class="gl-mx-auto" size="lg" />

    <div v-else-if="!error">
      <div v-if="showHeader" class="gl-flex gl-items-center gl-justify-between gl-mb-5">
        <div class="gl-flex gl-items-center">
          <gl-icon class="gl-mr-3" name="text-description" />
          <h3 data-testid="billing-account-details-header">{{ __('Customer information') }}</h3>
        </div>
        <gl-link :href="$options.BILLING_ACCOUNT_PATH" class="gl-text-inherit">
          <gl-icon class="gl-mr-3 gl-mb-1" name="pencil-square" />{{ __('Edit in settings') }}
        </gl-link>
      </div>

      <div>
        <contact-billing-address
          data-testid="sold_to_information"
          class="gl-mb-5"
          :title="__(subscriptionContactTitle)"
          :contact="billingAccount.soldToContact"
        />
        <contact-billing-address
          v-if="showBillingContact"
          data-testid="bill_to_information"
          class="gl-mb-5"
          :title="__($options.i18n.billingContactTitle)"
          :contact="billingAccount.billToContact"
        />
        <company-information
          :zuora-account-name="billingAccount.zuoraAccountName"
          :vat-code="billingAccount.zuoraAccountVatId"
          :show-vat-code="billingAccount.vatFieldVisible"
        />
      </div>
    </div>
  </div>
</template>
